import { useState } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useDesignSystem } from '../../hooks/useDesignSystem';
import { useAuth } from '../../context/AuthContext';
import { useChangePassword } from 'hooks/auth/useChangePassword';
import { useSnackbar } from 'context/SnackbarContext';
import { normalizeApiError } from 'helpers/normalizeApiError';
import { validatePassword, isPasswordValid } from 'helpers/passwordValidator';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { changePasswordI18n } from './i18n';
import '../Login/login.scss';

const ChangePassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearMustChangePassword } = useAuth();
  const { changePassword, isLoading } = useChangePassword();
  const { showSnackbar } = useSnackbar();
  const DesignSystemComponents = useDesignSystem();
  const { t } = useScopedTranslation(changePasswordI18n);

  const oldPassword = location.state?.oldPassword;

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  if (!oldPassword) {
    return <Navigate to='/login' replace />;
  }

  const ruleLabels = {
    minLength: t('cp_rule_minLength'),
    uppercase: t('cp_rule_uppercase'),
    special: t('cp_rule_special'),
    noConsecutiveSpecial: t('cp_rule_noConsecutiveSpecial'),
  };

  const passwordRules = validatePassword(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!newPassword.trim() || !confirmNewPassword.trim()) {
      showSnackbar(t('cp_err_required'), 'error');
      return;
    }

    if (!isPasswordValid(newPassword)) {
      showSnackbar(t('cp_err_invalid'), 'error');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      showSnackbar(t('cp_err_mismatch'), 'error');
      return;
    }

    try {
      await changePassword(oldPassword, newPassword);
      clearMustChangePassword();
      navigate('/');
    } catch (err) {
      const normalized = normalizeApiError(err, t('cp_err_default'));
      showSnackbar(normalized.message, 'error');
    }
  };

  if (!DesignSystemComponents) {
    return (
      <div className='login-container-loading'>
        <div className='loading-spinner' />
      </div>
    );
  }

  const { Card, Input, Button } = DesignSystemComponents;

  return (
    <div className='min-vh-100 d-flex align-items-center justify-content-center background-cover'>
      <Card className='card-login'>
        <div>
          <div className='text-center mb-6'>
            <p className='caption color-gray-600 mb-2 text-center'>{t('cp_brand')}</p>
            <h1 className='h4 mb-1 text-center'>{t('cp_title')}</h1>
            <p className='caption color-gray-600 mb-0 text-center'>
              {t('cp_subtitle')}
            </p>
          </div>
          <form className='d-flex flex-column' onSubmit={handleSubmit} noValidate>
            <div>
              <Input
                label={t('cp_newPassword')}
                id='newPassword'
                type={showNewPassword ? 'text' : 'password'}
                placeholder={t('cp_newPasswordPlaceholder')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                icon={showNewPassword ? 'eye' : 'eyeSlash'}
                iconOnClick={() => setShowNewPassword((prev) => !prev)}
              />
            </div>
            <div>
              <Input
                label={t('cp_confirmPassword')}
                id='confirmNewPassword'
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('cp_confirmPasswordPlaceholder')}
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                disabled={isLoading}
                icon={showConfirmPassword ? 'eye' : 'eyeSlash'}
                iconOnClick={() => setShowConfirmPassword((prev) => !prev)}
              />
            </div>
            {newPassword && (
              <div className='mt-2 mb-2'>
                <p className='mini fw-semibold mb-1'>{t('cp_rule_title')}</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {passwordRules.map(({ key, passed }) => (
                    <li
                      key={key}
                      className='mini d-flex align-items-center gap-1'
                      style={{ color: passed ? 'var(--success-500, #198754)' : 'var(--error-500, #dc3545)' }}
                    >
                      <span>{passed ? '✓' : '✗'}</span>
                      <span>{ruleLabels[key]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className='d-flex align-items-center justify-content-between mt-3'>
              <Button
                type='submit'
                label={isLoading ? t('cp_submitting') : t('cp_submit')}
                variant='primary'
                size='sm'
                disabled={isLoading || !isPasswordValid(newPassword)}
              />
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default ChangePassword;
